import { pdf } from "@react-pdf/renderer"
import { InventoryReportPdfDocument } from "../../utils/InventoryReportPdfDocument"
import type { ItemListSummaryRow } from "../types/listItemSummary"
import { computeKpi, formatNumberId } from "./listItemSummaryHelpers"
import { buildExportRows, EXPORT_HEADERS } from "./listItemSummaryExport.common"

const COLUMN_FLEX = [2, 4, 10, 5, 5, 2, 2, 4, 4, 4, 5, 3, 4, 4]

export async function exportItemListSummaryPdf(params: {
  periodLabel: string
  rows: ItemListSummaryRow[]
  orientation: "portrait" | "landscape"
  fileName?: string
}): Promise<void> {
  const { periodLabel, rows, orientation, fileName } = params
  const kpi = computeKpi(rows)
  const body = buildExportRows(rows).map((row) => row.map((c) => String(c)))

  const kpiHeaders = [
    "Total SKU",
    "Total SOH",
    "Inventory Value",
    "Low Stock",
    "Out of Stock",
    "Dead Stock Value",
    "Avg Margin"
  ]
  const kpiValues = [
    String(kpi.totalSku),
    formatNumberId(kpi.totalSoh),
    formatNumberId(kpi.inventoryValueTotal),
    String(kpi.lowStock),
    String(kpi.outOfStock),
    formatNumberId(kpi.deadStockValue),
    kpi.avgMarginPct !== null ? `${kpi.avgMarginPct.toFixed(0)}%` : "-"
  ]

  const columnAlign = EXPORT_HEADERS.map((_, i) =>
    [6, 7, 8, 9, 10, 11].includes(i) ? ("right" as const) : ("left" as const)
  )

  const firstPageRowLimit = orientation === "landscape" ? 16 : 11
  const otherPageRowLimit = orientation === "landscape" ? 28 : 20

  const doc = (
    <InventoryReportPdfDocument
      orientation={orientation}
      documentTitle="Item List Summary"
      heading="Inventory Report"
      subtitleLines={[`Item List Summary | Period: ${periodLabel}`]}
      kpiHeaders={kpiHeaders}
      kpiValues={kpiValues}
      tableHeaders={EXPORT_HEADERS}
      tableRows={body}
      columnFlex={COLUMN_FLEX}
      columnAlign={columnAlign}
      firstPageRowLimit={firstPageRowLimit}
      otherPageRowLimit={otherPageRowLimit}
    />
  )

  const blob = await pdf(doc).toBlob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName ?? "Inventory_Item_List_Summary.pdf"
  link.click()
  URL.revokeObjectURL(url)
}
